export interface ActivityLike { id: string; parentId: string | null; sortIndex: number; isSummary?: boolean; isCritical?: boolean }

export interface TreeNode<A extends ActivityLike = ActivityLike> {
  activity: A
  children: TreeNode<A>[]
  parent: TreeNode<A> | null
  depth: number
  /** 1-based position in the full display order — the MS Project "ID" column; independent of collapsing. */
  rowNumber: number
}
export interface Tree<A extends ActivityLike = ActivityLike> {
  byId: Map<string, TreeNode<A>>
  roots: TreeNode<A>[]
  order: TreeNode<A>[]
  rowNumberById: Map<string, number>
  idByRowNumber: Map<number, string>
}

const bySort = <A extends ActivityLike>(a: TreeNode<A>, b: TreeNode<A>) =>
  a.activity.sortIndex - b.activity.sortIndex || a.activity.id.localeCompare(b.activity.id)

export function buildTree<A extends ActivityLike>(activities: A[]): Tree<A> {
  const byId = new Map<string, TreeNode<A>>()
  for (const a of activities) byId.set(a.id, { activity: a, children: [], parent: null, depth: 0, rowNumber: 0 })
  const roots: TreeNode<A>[] = []
  byId.forEach((node) => {
    const parent = node.activity.parentId ? byId.get(node.activity.parentId) : undefined
    if (parent && parent !== node) { node.parent = parent; parent.children.push(node) } else roots.push(node)
  })
  roots.sort(bySort)
  const order: TreeNode<A>[] = []
  const visit = (node: TreeNode<A>, depth: number) => {
    node.depth = depth
    node.rowNumber = order.length + 1
    order.push(node)
    node.children.sort(bySort)
    for (const c of node.children) visit(c, depth + 1)
  }
  for (const r of roots) visit(r, 0)
  return {
    byId, roots, order,
    rowNumberById: new Map(order.map((n) => [n.activity.id, n.rowNumber])),
    idByRowNumber: new Map(order.map((n) => [n.rowNumber, n.activity.id])),
  }
}

export interface VisibleRow<A extends ActivityLike = ActivityLike> { node: TreeNode<A>; index: number; hasChildren: boolean; collapsed: boolean }

/** DFS over the tree skipping collapsed subtrees. With `filter`, a node is shown when it or any descendant passes. */
export function visibleRows<A extends ActivityLike>(tree: Tree<A>, collapsedIds: Set<string>, filter?: (node: TreeNode<A>) => boolean): VisibleRow<A>[] {
  let shown: Set<string> | null = null
  if (filter) {
    shown = new Set<string>()
    for (let i = tree.order.length - 1; i >= 0; i--) {
      const n = tree.order[i]
      if (filter(n) || n.children.some((c) => shown!.has(c.activity.id))) shown.add(n.activity.id)
    }
  }
  const rows: VisibleRow<A>[] = []
  const visit = (node: TreeNode<A>) => {
    if (shown && !shown.has(node.activity.id)) return
    const hasChildren = node.children.length > 0
    const collapsed = hasChildren && collapsedIds.has(node.activity.id)
    rows.push({ node, index: rows.length, hasChildren, collapsed })
    if (!collapsed) for (const c of node.children) visit(c)
  }
  for (const r of tree.roots) visit(r)
  return rows
}

/** Nearest ancestor first. */
export const ancestorIds = (tree: Tree<any>, id: string): string[] => {
  const out: string[] = []
  let n = tree.byId.get(id)?.parent ?? null
  while (n) { out.push(n.activity.id); n = n.parent }
  return out
}

/** The node and its whole subtree in display order. */
export const subtreeIds = (tree: Tree<any>, id: string): string[] => {
  const out: string[] = []
  const visit = (n: TreeNode<any>) => { out.push(n.activity.id); n.children.forEach(visit) }
  const n = tree.byId.get(id)
  if (n) visit(n)
  return out
}

/** The row that stands for `id` on screen: itself, or the outermost collapsed ancestor hiding it. */
export const visibleRowOf = (tree: Tree<any>, collapsedIds: Set<string>, id: string): string => {
  let result = id
  for (const a of ancestorIds(tree, id)) if (collapsedIds.has(a)) result = a
  return result
}
